import { Button } from '@mui/material';
import Dialog from 'components/dialog';
import FilterSidebar from 'components/filter-sidebar';
import LoadingSpinner from 'components/loading-spinner';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { useCallback, useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { usePondsState } from 'views/production-systems/context/ponds-context';
import AddFeeding from 'views/production-systems/features/farm-overview/components/add-feeding/add-feeding';
import AddHarvest from 'views/production-systems/features/farm-overview/components/add-harvest/add-harvest';
import AddSampling from 'views/production-systems/features/farm-overview/components/add-sampling/add-sampling';
import AddStocking from 'views/production-systems/features/farm-overview/components/add-stocking/add-stocking';
import AddTransfer from 'views/production-systems/features/farm-overview/components/add-transfer/add-transfer';
import AddTreatment from 'views/production-systems/features/farm-overview/components/add-treatment/add-treatment';
import AddWaterQuality from 'views/production-systems/features/farm-overview/components/add-water-quality/add-water-quality';
import { Pond } from 'views/production-systems/types/pond.type';

const PAGE_NAME_ID = 'productionSystems.farmOverview';

type SortablePond = Pond & {
  biomass: number;
  status: boolean;
};

interface Props {
  renderPonds: SortablePond[];
  ponds: SortablePond[];
  loadingPonds: boolean;
}

const ListPondsActions: React.FC<Props> = ({ renderPonds, ponds, loadingPonds }) => {
  const intl = useIntl();
  const { selectedPonds, lockedPonds, releasePonds, lockPonds, error } = usePondsState();
  const snackbarDispatch = useSnackbarDispatch();

  const createStockingDialogRef = useRef<React.ElementRef<typeof Dialog>>(null);
  const createHarvestDialogRef = useRef<React.ElementRef<typeof Dialog>>(null);
  const createFeedingDialogRef = useRef<React.ElementRef<typeof Dialog>>(null);
  const createSamplingDialogRef = useRef<React.ElementRef<typeof Dialog>>(null);
  const createTransferDialogRef = useRef<React.ElementRef<typeof Dialog>>(null);
  const createWaterQualityDialogRef = useRef<React.ElementRef<typeof Dialog>>(null);
  const createTreatmentDialogRef = useRef<React.ElementRef<typeof Dialog>>(null);

  const isActionEnabled = useCallback(
    (action: 'allowHarvest' | 'allowStocking' | 'allowTransfer' | 'allowOtherRegistrations') => {
      let ponds = renderPonds.filter((p) => selectedPonds.includes(p.pondId));
      let allPondActionsPassed = !ponds.every((pond) => pond[action]);

      if (action === 'allowTransfer') {
        return !(selectedPonds.length === 1) || allPondActionsPassed;
      } else {
        return !(selectedPonds.length > 0) || allPondActionsPassed;
      }
    },
    [selectedPonds, ponds]
  );

  const handleDialogClose = () => {
    if (!!lockedPonds?.granted.length || !!lockedPonds?.notGranted.length) {
      releasePonds(lockedPonds.granted);
    }

    createStockingDialogRef.current?.setOpen(false);
    createHarvestDialogRef.current?.setOpen(false);
    createFeedingDialogRef.current?.setOpen(false);
    createSamplingDialogRef.current?.setOpen(false);
    createTransferDialogRef.current?.setOpen(false);
    createWaterQualityDialogRef.current?.setOpen(false);
    createTreatmentDialogRef.current?.setOpen(false);
  };

  useEffect(() => {
    if (error) {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
      handleDialogClose();
    }
  }, [error]);

  const handleClick = () => {
    lockPonds(selectedPonds);
  };

  return (
    <FilterSidebar sx={{ paddingTop: 2 }}>
      <Dialog
        ref={createStockingDialogRef}
        onClose={handleDialogClose}
        title={intl.formatMessage({ id: `${PAGE_NAME_ID}.buttons.newStock` })}
        maxWidth="xl"
        button={
          <Button onClick={handleClick} disabled={isActionEnabled('allowStocking')} sx={{ width: { xs: '100%', md: 'auto' } }}>
            <FormattedMessage id={`${PAGE_NAME_ID}.buttons.newStock`} />
          </Button>
        }>
        {loadingPonds ? (
          <LoadingSpinner />
        ) : (
          <AddStocking ponds={ponds?.filter((p) => selectedPonds.includes(p.pondId)) || []} handleClose={handleDialogClose} />
        )}
      </Dialog>
      <Dialog
        ref={createTransferDialogRef}
        onClose={handleDialogClose}
        title={intl.formatMessage({ id: `${PAGE_NAME_ID}.buttons.newTransfer` })}
        maxWidth="xl"
        button={
          <Button onClick={handleClick} disabled={isActionEnabled('allowTransfer')} sx={{ width: { xs: '100%', md: 'auto' } }}>
            <FormattedMessage id={`${PAGE_NAME_ID}.buttons.newTransfer`} />
          </Button>
        }>
        {loadingPonds ? (
          <LoadingSpinner />
        ) : (
          <AddTransfer handleClose={handleDialogClose} selectedPond={ponds.find((p) => selectedPonds[0] === p.pondId)} />
        )}
      </Dialog>
      <Dialog
        ref={createHarvestDialogRef}
        onClose={handleDialogClose}
        title={intl.formatMessage({ id: `${PAGE_NAME_ID}.buttons.newHarvest` })}
        maxWidth="xl"
        button={
          <Button onClick={handleClick} disabled={isActionEnabled('allowHarvest')} sx={{ width: { xs: '100%', md: 'auto' } }}>
            <FormattedMessage id={`${PAGE_NAME_ID}.buttons.newHarvest`} />
          </Button>
        }>
        {loadingPonds ? (
          <LoadingSpinner />
        ) : (
          <AddHarvest ponds={ponds?.filter((p) => selectedPonds.includes(p.pondId)) || []} handleClose={handleDialogClose} />
        )}
      </Dialog>
      <Dialog
        ref={createFeedingDialogRef}
        onClose={handleDialogClose}
        title={intl.formatMessage({ id: `${PAGE_NAME_ID}.buttons.newFeeding` })}
        maxWidth="xl"
        button={
          <Button
            onClick={handleClick}
            disabled={isActionEnabled('allowOtherRegistrations')}
            sx={{ width: { xs: '100%', md: 'auto' } }}>
            <FormattedMessage id={`${PAGE_NAME_ID}.buttons.newFeeding`} />
          </Button>
        }>
        {loadingPonds ? (
          <LoadingSpinner />
        ) : (
          <AddFeeding ponds={ponds?.filter((p) => selectedPonds.includes(p.pondId)) || []} handleClose={handleDialogClose} />
        )}
      </Dialog>
      <Dialog
        ref={createSamplingDialogRef}
        onClose={handleDialogClose}
        title={intl.formatMessage({ id: `${PAGE_NAME_ID}.buttons.sampling` })}
        maxWidth="xl"
        button={
          <Button
            onClick={handleClick}
            disabled={isActionEnabled('allowOtherRegistrations')}
            sx={{ width: { xs: '100%', md: 'auto' } }}>
            <FormattedMessage id={`productionSystems.registrationLog.registrationTypes.meanweightSample`} />
          </Button>
        }>
        {loadingPonds ? (
          <LoadingSpinner />
        ) : (
          <AddSampling ponds={ponds?.filter((p) => selectedPonds.includes(p.pondId)) || []} handleClose={handleDialogClose} />
        )}
      </Dialog>
      <Dialog
        ref={createWaterQualityDialogRef}
        onClose={handleDialogClose}
        title={intl.formatMessage({ id: `${PAGE_NAME_ID}.buttons.waterQuality` })}
        maxWidth="xl"
        button={
          <Button
            onClick={handleClick}
            disabled={isActionEnabled('allowOtherRegistrations')}
            sx={{ width: { xs: '100%', md: 'auto' } }}>
            <FormattedMessage id={`${PAGE_NAME_ID}.buttons.waterQuality`} />
          </Button>
        }>
        {loadingPonds ? (
          <LoadingSpinner />
        ) : (
          <AddWaterQuality ponds={ponds?.filter((p) => selectedPonds.includes(p.pondId)) || []} handleClose={handleDialogClose} />
        )}
      </Dialog>
      <Dialog
        ref={createTreatmentDialogRef}
        onClose={handleDialogClose}
        title={intl.formatMessage({ id: `${PAGE_NAME_ID}.buttons.newTreatment` })}
        maxWidth="xl"
        button={
          <Button
            onClick={handleClick}
            disabled={isActionEnabled('allowOtherRegistrations')}
            sx={{ width: { xs: '100%', md: 'auto' } }}>
            <FormattedMessage id={`${PAGE_NAME_ID}.buttons.newTreatment`} />
          </Button>
        }>
        {loadingPonds ? (
          <LoadingSpinner />
        ) : (
          <AddTreatment ponds={ponds?.filter((p) => selectedPonds.includes(p.pondId)) || []} handleClose={handleDialogClose} />
        )}
      </Dialog>
    </FilterSidebar>
  );
};

export default ListPondsActions;
